import { ClientModel } from "@/interfaces/thirdParty";
import HttpService from "./http.service";
import { messagesService } from "./messages.service";

class ThirdPartyService extends HttpService {
  protected uri = "auth/apps";
  protected headers = {
    Accept: "application/json"
  };

  public async get(
    query: { page: number; per_page: number } | null
  ): Promise<ClientModel[]> {
    this.uri = "auth/apps";
    if (query) {
      this.uri += `?per_page=${query.per_page}&page=${query.page}`;
    }
    try {
      const response = await this.axios.request({
        url: `${this.uri}`,
        method: "GET",
        headers: this.headers,
        data: {
          query: query || this.query
        }
      });
      this.setPagination(response.data);
      return response.data.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return [];
    }
  }
  public async getSecret(clientID: string): Promise<string> {
    try {
      const response = await this.axios.request({
        url: `auth/apps/${clientID}/secret`,
        method: "GET",
        headers: this.headers
      });
      return response.data.data.app_secret;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return "";
    }
  }

  public async regenerate(
    clientID: string
  ): Promise<{
    app_secret: string;
    app_id: string;
    oauth_server_url: string;
  } | null> {
    try {
      this.uri = "auth/apps";
      this.uri = `${this.uri}/${clientID}/regenerate`;
      const response = await this.axios.request({
        url: this.uri,
        method: "POST",
        headers: this.headers,
        data: {
          query: this.query
        }
      });
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return null;
    }
  }

  public async create(client: { name: string }): Promise<ClientModel | null> {
    try {
      const response = await this.axios.request({
        url: this.uri,
        method: "POST",
        headers: this.headers,
        data: {
          name: client.name
        }
      });
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return null;
    }
  }

  public async update(client: {
    id: string;
    name: string;
    call_back_url: string;
    scopes: Array<{ id: number }>;
  }): Promise<boolean> {
    try {
      this.uri = `auth/apps/${client.id}`;
      const response = await this.axios.request({
        url: this.uri,
        method: "PUT",
        headers: this.headers,
        data: {
          name: client.name,
          scopes: client.scopes,
          call_back_url: client.call_back_url
        }
      });
      return response.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return false;
    }
  }

  public async toggleIntegration(
    clientID: string,
    state: boolean
  ): Promise<void> {
    try {
      const response = await this.axios.request({
        url: `${this.uri}/${clientID}/${state ? "enable" : "disable"}`,
        method: "PUT",
        headers: this.headers
      });
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  }

  public async delete(clientID: string): Promise<boolean> {
    try {
      const response = await this.axios.request({
        url: `${this.uri}/${clientID}`,
        method: "DELETE",
        headers: this.headers
      });
      return !!response;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return false;
    }
  }

  public async getScopes() {
    try {
      const response = await this.axios.request({
        url: `${this.uri}/scopes`,
        method: "GET",
        headers: this.headers
      });
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return null;
    }
  }

  public async getClient(clientID: string): Promise<ClientModel> {
    try {
      const response = await this.axios.request({
        url: `${this.uri}/${clientID}`,
        method: "GET",
        headers: this.headers
      });
      return response.data.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
      return {
        id: "",
        name: "",
        credentials: {
          app_id: "",
          app_secret: "",
          oauth_server_url: ""
        },
        call_back_url: "",
        role_id: 0,
        status: "",
        scopes: []
      };
    }
  }
}

export const thirdPartyService = new ThirdPartyService();
